/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import Add from '@mui/icons-material/Add';
import Language from '@mui/icons-material/Language';
import Remove from '@mui/icons-material/Remove';
import { isNil } from 'lodash';
import React, { useMemo } from 'react';
import { withTheme } from 'styled-components';
import GoogleMapsControl, { MapControlOrderIndex } from '../map/GoogleMapsControl';
import { INITIAL_LATITUDE, INITIAL_LONGITUDE, INITIAL_ZOOM } from '../map/BaseMapComponent';
import StyledProps from '../theme/AppThemeInterface';
import { Tooltip } from '@mui/material';

interface ZoomMapControlProps extends StyledProps {
  map: google.maps.Map;
  addMarginTop?: boolean;
}

const ZoomMapControl = (props: ZoomMapControlProps) => {
  const { theme, map, addMarginTop } = props;
  const items = useMemo(() => {
    let response: {
      render: Function;
      buttonTextColor: string;
      id: string;
      onClick: Function;
      isSmallSize?: boolean;
    }[] = [];
    if (map) {
      response = [
        {
          render: () => (
            <ZoomMapIconContainer title="Zoom In" theme={theme}>
              <Add />
            </ZoomMapIconContainer>
          ),
          buttonTextColor: theme.secondaryButtonTextColor,
          id: 'mapZoomIn',
          onClick: () => {
            const currentZoom: number | null | undefined = map.getZoom();
            if (!isNil(currentZoom)) {
              map.setZoom(currentZoom + 1);
            }
          },
        },
        {
          render: () => (
            <ZoomMapIconContainer title="Zoom Out" theme={theme}>
              <Remove />
            </ZoomMapIconContainer>
          ),
          buttonTextColor: theme.secondaryButtonTextColor,
          id: 'mapZoomOut',
          onClick: () => {
            const currentZoom: number | null | undefined = map.getZoom();
            if (!isNil(currentZoom)) {
              map.setZoom(currentZoom - 1);
            }
          },
        },
        {
          render: () => (
            <ZoomMapIconContainer title="World View" theme={theme}>
              <Language fontSize="small" />
            </ZoomMapIconContainer>
          ),

          buttonTextColor: theme.secondaryButtonTextColor,
          id: 'mapZoomToWorldView',
          isSmallSize: true,
          onClick: () => {
            map.panTo({ lat: INITIAL_LATITUDE, lng: INITIAL_LONGITUDE });
            map.setZoom(INITIAL_ZOOM);
          },
        },
      ];
    }
    return response;
  }, [map, theme]);

  return (
    <GoogleMapsControl
      map={map}
      items={items}
      mapPosition={google.maps.ControlPosition.RIGHT_TOP}
      height={96}
      index={MapControlOrderIndex.ZOOM_CONTROL}
      marginTop={addMarginTop ? 12 : 0}
    />
  );
};

export default withTheme(ZoomMapControl);

interface ZoomMapIconContainerProps extends StyledProps {
  title: string;
  children: JSX.Element;
}
export const ZoomMapIconContainer = ({ title, children, theme }: ZoomMapIconContainerProps) => {
  return (
    <Tooltip
      arrow
      title={title}
      placement="left-start"
      componentsProps={{
        tooltip: {
          sx: { backgroundColor: theme.tooltipBackgroundColor },
        },
        arrow: {
          sx: { color: theme.tooltipBackgroundColor },
        },
      }}
    >
      {children}
    </Tooltip>
  );
};
